import type { MilkdownPlugin } from '@milkdown/kit/ctx';
import type { Root } from 'mdast';
import { $remark } from '@milkdown/kit/utils';
import { visitParents } from 'unist-util-visit-parents';

/**
 * Legacy Brainfish / rich-markdown-editor hard break escape in stored markdown (` \n `).
 * Matches platform `shared/editor/rules/tables.ts` BREAK_REGEX.
 */
export const LEGACY_HARD_BREAK_REGEX = /(?:^|[^\\])\\n/;

type LegacyHardBreakSegment = { kind: 'text'; value: string } | { kind: 'break' };

/** Split text on unescaped `\n` sequences; each delimiter becomes a hard break. */
export function splitLegacyHardBreaks(value: string): LegacyHardBreakSegment[] {
  if (!LEGACY_HARD_BREAK_REGEX.test(value)) {
    return [{ kind: 'text', value }];
  }

  const parts = value.split(LEGACY_HARD_BREAK_REGEX);
  const out: LegacyHardBreakSegment[] = [];

  for (let i = 0; i < parts.length; i++) {
    const trimmed = parts[i].trim();
    if (trimmed) {
      out.push({ kind: 'text', value: trimmed });
    }
    if (i < parts.length - 1) {
      out.push({ kind: 'break' });
    }
  }

  return out.length ? out : [{ kind: 'text', value }];
}

/** Rewrite mdast text nodes containing legacy `\n` into text + `break` siblings. */
export function transformLegacyHardBreaks(tree: Root): void {
  visitParents(tree, 'text', (node, ancestors) => {
    if (!node.value || !LEGACY_HARD_BREAK_REGEX.test(node.value)) return;
    if (ancestors.some((a) => (a as { type?: string }).type === 'code')) return;

    const parent = ancestors[ancestors.length - 1] as { children?: unknown[] };
    if (!parent?.children || !Array.isArray(parent.children)) return;

    const index = parent.children.indexOf(node);
    if (index === -1) return;

    const segments = splitLegacyHardBreaks(node.value);
    if (segments.length === 1 && segments[0].kind === 'text') return;

    const replacements: Array<{ type: 'text'; value: string } | { type: 'break' }> = [];
    for (const seg of segments) {
      if (seg.kind === 'text') {
        replacements.push({ type: 'text', value: seg.value });
      } else {
        replacements.push({ type: 'break' });
      }
    }

    parent.children.splice(index, 1, ...replacements);
  });
}

export const remarkLegacyHardBreak = $remark('brainfish-legacy-hard-break', () => {
  return () => (tree: Root) => {
    transformLegacyHardBreaks(tree);
  };
});

export const legacyHardBreakPlugin: MilkdownPlugin[] = [remarkLegacyHardBreak].flat();
